import Alpine from "alpinejs";

const tabsController = {
    // Set up active Tabs Controller
    groups: new Map(),

    getGroup(groupId) {
        if (!this.groups.has(groupId)) {
            this.groups.set(groupId, {
                activeTab: 0,
                tabs: [],
                panels: []
            });
            Alpine.store(`tabs-${groupId}`, {
                activeTab: 0
            });
        }
        return this.groups.get(groupId);
    },
    registerTab(groupId, element, index) {
        const group = this.getGroup(groupId);
        if (!group.tabs[index]) {
            group.tabs[index] = [];
        }
        group.tabs[index].push(element);
    },
    registerPanel(groupId, element, index) {
        const group = this.getGroup(groupId);

        if (!group.panels[index]) {
            group.panels[index] = [];
        }
        group.panels[index].push(element);
    },
    activate(groupId, index) {
        const group = this.getGroup(groupId);

        group.activeTab = index;

        Alpine.store(`tabs-${groupId}`).activeTab = index;

        this.updateButtons(groupId);
        this.updatePanels(groupId);

        Alpine.nextTick(() => this.centerActiveTab(groupId, index));
    },
    centerActiveTab(groupId, index) {
        const group = this.getGroup(groupId);
        const tabButtons = group.tabs[index];
        if (!tabButtons || tabButtons.length === 0) return;

        let activeButton = null;
        for (const btn of tabButtons) {
            if (btn.offsetParent !== null) {
                activeButton = btn;
                break;
            }
        }
        if (!activeButton) return;

        const tabsStrip = activeButton.closest(".profileTabsV2__tabs");
        if (!tabsStrip) return;

        if (tabsStrip.classList.contains("profileTabsV2__tabs--double") && window.innerWidth <= 450) {
            const sliver = 20;
            const targetScroll = Math.max(0, activeButton.offsetLeft - sliver);
            tabsStrip.scrollTo({ left: targetScroll, behavior: "smooth" });
            return;
        }

        const containerRect = tabsStrip.getBoundingClientRect();
        const tabRect = activeButton.getBoundingClientRect();
        const offset = tabRect.left - containerRect.left - tabsStrip.offsetWidth / 2 + activeButton.offsetWidth / 2;

        tabsStrip.scrollBy({ left: offset, behavior: "smooth" });
    },
    nextTab(groupId, options = {}) {
        const group = this.getGroup(groupId);
        const totalTabs = group.tabs.length;

        if (totalTabs === 0) return;

        let nextIndex;
        if (group.activeTab === null) {
            nextIndex = 0;
        } else {
            nextIndex = (group.activeTab + 1) % totalTabs;
        }
        this.activate(groupId, nextIndex);

        if (options.scroll !== false) {
            this.scrollToTab(groupId, nextIndex, options);
        }
    },
    getResponsiveOffset(options) {
        if (typeof options.offset === "number") {
            return options.offset;
        }
        if (typeof options.offset === "object") {
            const width = window.innerWidth;

            if (options.offset.desktop !== undefined && width >= 768) {
                return options.offset.desktop;
            }
            if (options.offset.mobile !== undefined && width < 768) {
                return options.offset.mobile;
            }
            return options.offset.base || 0;
        }
        return 0;
    },
    scrollToTab(groupId, index, options = {}) {
        const group = this.getGroup(groupId);
        const tabButtons = group.tabs[index];

        if (!tabButtons || tabButtons.length === 0) return;

        let visibleButton = null;
        for (const btn of tabButtons) {
            if (btn.offsetParent !== null) {
                visibleButton = btn;
                break;
            }
        }

        if (!visibleButton) return;

        const tabContainer = visibleButton.closest("[data-tabs-container]");

        if (!tabContainer) return;

        const scrollBehavior = {
            behavior: options.behavior || "smooth",
            block: options.block || "start",
            inline: options.inline || "nearest"
        };

        const offset = this.getResponsiveOffset(options);

        if (offset > 0) {
            const elementPosition = tabContainer.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - offset;

            window.scrollTo({
                top: offsetPosition,
                behavior: scrollBehavior.behavior
            });
        } else {
            tabContainer.scrollIntoView(scrollBehavior);
        }
    },

    updateButtons(groupId) {
        const group = this.getGroup(groupId);
        group.tabs.forEach((buttons, i) => {
            if (!buttons) return;

            buttons.forEach(btn => {
                if (!btn) return;
                btn.classList.toggle("active", i === group.activeTab);
            });
        });
    },
    updatePanels(groupId) {
        const group = this.getGroup(groupId);
        group.panels.forEach((panels, i) => {
            if (!panels) return;

            panels.forEach(panel => {
                if (!panel) return;
                panel.style.display = i === group.activeTab ? "block" : "none";
            });
        });
    }
};
window.addEventListener("tab-next", event => {
    const groupId = event.detail?.groupId || "default";
    const options = {
        scroll: event.detail?.scroll !== false,
        behavior: event.detail?.behavior || "smooth",
        block: event.detail?.block || "start",
        offset: event.detail?.offset || 0
    };
    tabsController.nextTab(groupId, options);
});

document.addEventListener("alpine:init", () => {
    Alpine.directive("tab", (el, { modifiers, expression }) => {
        const groupId = modifiers[0] || "default";
        const index = parseInt(expression);
        const isAccordion = modifiers.includes("accordion");

        tabsController.registerTab(groupId, el, index);

        el.addEventListener("click", () => {
            // If accordion mode and tab is already active, toggle it closed
            if (isAccordion) {
                const group = tabsController.getGroup(groupId);
                if (group.activeTab === index) {
                    group.activeTab = null;
                    Alpine.store(`tabs-${groupId}`).activeTab = null;
                    tabsController.updateButtons(groupId);
                    tabsController.updatePanels(groupId);
                    return;
                }
            }
            // Normal behavior: activate the tab
            tabsController.activate(groupId, index);
        });

        if (index === 0) {
            el.classList.add("active");
        }
    });

    Alpine.directive("tab-panel", (el, { modifiers, expression }) => {
        const groupId = modifiers[0] || "default";
        const index = parseInt(expression);

        tabsController.registerPanel(groupId, el, index);

        el.style.display = index !== 0 ? "none" : "block";
    });
});
